/** Publisher and creator record pages. They have no cover; the gauge shows beside the title. */
import { Link, useParams, useSearchParams } from 'react-router'
import { BookShelf, SeriesShelf, ShelfSkeleton, SystemShelf } from '../components/Cards'
import { Empty, ErrorState, Loading, Pager, usePage } from '../components/Bits'
import { Attributions, GapList, LinkList, MergedRedirect, Prose, RecordHeader } from '../components/Record'
import { ClaimButton, VerifiedBadge } from '../components/RecordActions'
import { qs } from '../lib/api'
import { plural, ROLE_LABELS } from '../lib/format'
import { useQuery } from '../lib/query'
import { useTitle } from '../lib/useTitle'
import type { BookCard, CreatorDetail, Page, PublisherDetail } from '../lib/types'
import type { CreatorDoc, CreditRole, PublisherDoc } from '../../shared/model'
import { Portrait } from '../components/Portrait'
import { RoleIcon } from '../components/RoleIcon'

function BooksSection({ path, title, role }: { path: string; title: string; role?: string }) {
  const page = usePage()
  const { data } = useQuery<Page<BookCard>>(`${path}${qs({ page, role, limit: 48 })}`)
  return (
    <section className="section">
      <h2 className="section-title">
        {title} {data && <span className="section-count">{plural(data.total, 'book')}</span>}
      </h2>
      {!data ? (
        <ShelfSkeleton count={6} />
      ) : data.items.length === 0 ? (
        <Empty title="No books yet" />
      ) : (
        <>
          <BookShelf items={data.items} />
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </section>
  )
}

export function PublisherPage() {
  const { id = '' } = useParams()
  const { data: p, error, refresh } = useQuery<PublisherDetail>(`/api/v1/publishers/${id}`)
  const doc = p?.doc as PublisherDoc | undefined
  useTitle(doc?.name)
  if (error) return <ErrorState error={error} retry={refresh} />
  if (!p || !doc) return <Loading label="Loading publisher" />
  if (p.merged_into) return <MergedRedirect type="publisher" to={p.merged_into} />
  return (
    <article className="page record">
      <RecordHeader
        type="publisher"
        id={p.id}
        actions={!p.verified && <ClaimButton type="publisher" id={p.id} name={doc.name} />}
        crumbs={[
          { label: 'Publishers', to: '/publishers' },
          ...(p.parent ? [{ label: p.parent.name, to: `/publishers/${p.parent.id}` }] : []),
        ]}
        title={doc.name}
        subtitle={doc.aliases.length ? `Also known as ${doc.aliases.join(', ')}` : undefined}
        hasCover={false}
        score={p.completeness.score}
        editHref={`/publishers/${p.id}/edit`}
        historyHref={`/publishers/${p.id}/history`}
        pending={p.pending_edits}
        pendingHref={`/review?entity=${p.id}`}
        deleted={p.deleted}
      >
        {p.verified && <VerifiedBadge />}
        {p.imprints.length > 0 && (
          <p className="record-byline">
            Imprints:{' '}
            {p.imprints.map((i, n) => (
              <span key={i.id}>
                {n > 0 && ', '}
                <Link to={`/publishers/${i.id}`}>{i.name}</Link>
              </span>
            ))}
          </p>
        )}
      </RecordHeader>
      <div className="record-body">
        <div className="record-main">
          <Prose text={doc.description} />
          <Attributions attributions={doc.attributions} />
          <GapList
            missing={p.completeness.missing}
            score={p.completeness.score}
            editHref={`/publishers/${p.id}/edit`}
          />
        </div>
        <aside className="record-side">
          <LinkList title="Links" links={doc.urls} />
        </aside>
      </div>
      {p.systems.length > 0 && (
        <section className="section">
          <h2 className="section-title">Systems</h2>
          <SystemShelf items={p.systems} />
        </section>
      )}
      {p.series.length > 0 && (
        <section className="section">
          <h2 className="section-title">Settings, series & lines</h2>
          <SeriesShelf items={p.series} />
        </section>
      )}
      <BooksSection path={`/api/v1/publishers/${p.id}/books`} title="Books" />
    </article>
  )
}

export function CreatorPage() {
  const { id = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const role = params.get('role') ?? ''
  const { data: c, error, refresh } = useQuery<CreatorDetail>(`/api/v1/creators/${id}`)
  const doc = c?.doc as CreatorDoc | undefined
  useTitle(doc?.name)
  if (error) return <ErrorState error={error} retry={refresh} />
  if (!c || !doc) return <Loading label="Loading creator" />
  if (c.merged_into) return <MergedRedirect type="creator" to={c.merged_into} />
  return (
    <article className="page record">
      <RecordHeader
        type="creator"
        id={c.id}
        actions={!c.verified && <ClaimButton type="creator" id={c.id} name={doc.name} />}
        crumbs={[{ label: 'Creators', to: '/creators' }]}
        title={doc.name}
        subtitle={
          c.roles.length > 0 && (
            <span className="role-list">
              {c.roles.map((r) => (
                <span key={r.role} className="role-chip">
                  <RoleIcon role={r.role} />
                  {ROLE_LABELS[r.role as CreditRole] ?? r.role} on {plural(r.count, 'book')}
                </span>
              ))}
            </span>
          )
        }
        hasCover={false}
        visual={<Portrait photo={doc.photo} name={doc.name} size="lg" />}
        score={c.completeness.score}
        editHref={`/creators/${c.id}/edit`}
        historyHref={`/creators/${c.id}/history`}
        pending={c.pending_edits}
        pendingHref={`/review?entity=${c.id}`}
        deleted={c.deleted}
      >
        {c.verified && <VerifiedBadge />}
        {doc.aliases.length > 0 && <p className="record-aliases">Also known as {doc.aliases.join(', ')}</p>}
      </RecordHeader>
      <div className="record-body">
        <div className="record-main">
          <Prose text={doc.description} />
          <Attributions attributions={doc.attributions} />
          <GapList missing={c.completeness.missing} score={c.completeness.score} editHref={`/creators/${c.id}/edit`} />
        </div>
        <aside className="record-side">
          <LinkList title="Links" links={doc.urls} />
        </aside>
      </div>
      {c.roles.length > 1 && (
        <div className="seg" role="group" aria-label="Filter by role">
          {[{ role: '', count: 0 }, ...c.roles].map((r) => (
            <button
              key={r.role || 'all'}
              type="button"
              aria-pressed={role === r.role}
              onClick={() => {
                const next = new URLSearchParams(params)
                if (r.role) next.set('role', r.role)
                else next.delete('role')
                next.delete('page')
                setParams(next, { replace: true })
              }}
            >
              {r.role ? (ROLE_LABELS[r.role as CreditRole] ?? r.role) : 'All roles'}
            </button>
          ))}
        </div>
      )}
      <BooksSection path={`/api/v1/creators/${c.id}/books`} title="Books" role={role || undefined} />
    </article>
  )
}
